import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { formatDate } from "@marekl/adminer/format";
import { AppTopbar } from "../../../shell/AppTopbar";
import {
  Page,
  Table,
  type Column,
  Input,
  Select,
  FilterField,
  FilterPanel,
  EmptyState,
  Pagination,
  Text,
  Button,
} from "@marekl/adminer/components";
import { MEMBERSHIP_KIND_LABELS } from "../../../lib/memberState";
import { MemberPhoto } from "../../../components/MemberPhoto";
import { RegisteredTick } from "../../../components/RegisteredTick";
import { useSeason } from "../../../season";
import {
  useOffsetPaginatedQuery,
  useDebouncedSearchFilter,
  useTableSort,
} from "@marekl/adminer/hooks";
import {
  useGlobalPersons,
  type PersonListItem,
} from "../../../lib/queries/persons";
import { EditPersonModal } from "./EditPersonModal";
import { AddToTeamModal } from "./AddToTeamModal";
import { PersonLink } from "../../../components/PersonDetail";
import { hasAnyGlobalRole, useMe } from "../../../auth/queries";
import type { ID } from "../../../lib/id";

const LIMIT_OPTIONS = [25, 50, 100, 200];
const DEFAULT_LIMIT = 50;

export function MembersRegistryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { data: me } = useMe();
  // Čtení členské základny and Čtení všeho read this whole page and write
  // nothing, so the row actions are the writers'.
  const canWrite = hasAnyGlobalRole(me, "admin", "members_admin");
  const [editing, setEditing] = useState<PersonListItem | null>(null);
  const [addingToTeam, setAddingToTeam] = useState<PersonListItem | null>(null);
  // Cache-buster per person. personPhotoUrl(id) is a stable URL, so a
  // replaced photo would keep rendering the old bytes until a hard
  // refresh — and this page can replace one, through the row's „Upravit"
  // dialog. Bumped when that dialog closes, the same way the roster does
  // it around PhotoModal.
  const [photoVersion, setPhotoVersion] = useState<Record<ID, number>>({});
  // Členství is derived against the ACTIVE season, so the column names it.
  const { activeSeason } = useSeason();

  const q = searchParams.get("q") ?? "";
  const member = (searchParams.get("member") ?? "") as "" | "1" | "0";

  // Hoisted for useTableSort. Teams and the login e-mail are filled in
  // AFTER the page is fetched (fillTeamsForPersons / fillLoginEmails), so
  // the server cannot order by them and the allow-list in persons.go
  // refuses them — they carry no `sortable`.
  //
  // Membership is the one exception, and it earns it: the „Členství"
  // filter already needs that EXISTS predicate, so expressing the sort
  // costs nothing new. Written up in docs/razeni.md. `sortField` names the
  // wire value where it differs from the column key.
  const columns = useMemo<Column<PersonListItem>[]>(
    () => [
      {
        // Read-only here. The roster's chip opens PhotoModal on click,
        // because there the viewer owns the soupiska; the registry is a
        // global list where a photo is something you recognise a person
        // by, not something you fix in passing. Editing one still goes
        // through the row's „Upravit" dialog.
        key: "photo",
        header: "Foto",
        render: (p) => (
          <MemberPhoto
            personId={p.id}
            firstName={p.first_name}
            lastName={p.last_name}
            hasPhoto={p.photo_uploaded}
            version={photoVersion[p.id] ?? 0}
          />
        ),
      },
      {
        key: "name",
        header: "Jméno",
        sortable: true,
        // Server-side: no sortValue (that is client mode). The wire name is
        // last_name, which the allow-list expands to last_name, first_name.
        sortField: "last_name",
        // The name opens the person detail dialog; editing stays on „Upravit".
        render: (p) => (
          <PersonLink personId={p.id}>
            {p.last_name} {p.first_name}
          </PersonLink>
        ),
      },
      {
        key: "cmf_id",
        header: "ČNSMS ID",
        sortable: true,
        render: (p) =>
          p.cmf_id ? (
            <Text variant="mono">{p.cmf_id}</Text>
          ) : (
            <Text variant="muted">—</Text>
          ),
      },
      {
        // Per-season, not permanent: someone who was a člen last season and has
        // not re-registered has no tick here, so the header names the season.
        key: "membership",
        header: activeSeason ? `Registrovaný (${activeSeason.code})` : "Registrovaný",
        // Sorted on the server, over the same EXISTS predicate the
        // „Členství" filter uses (personSort.is_member). The filter is
        // still the better control for „who is not registered yet" — a
        // boolean has two positions, so sorting only gathers the groups —
        // but the sort is what reads both of them in one list.
        sortable: true,
        sortField: "is_member",
        render: (p) => <RegisteredTick registered={p.is_member} />,
      },
      {
        key: "birth_date",
        header: "Datum narození",
        sortable: true,
        // Mono keeps the column aligned; the value is Czech, as everywhere
        // else. DD.MM.YYYY is fixed width, so alignment survives.
        render: (p) => (
          <Text variant="mono">{p.birth_date ? formatDate(p.birth_date) : "—"}</Text>
        ),
      },
      {
        key: "country_code",
        header: "Stát. přísl.",
        sortable: true,
        render: (p) => <Text variant="mono">{p.country_code}</Text>,
      },
      {
        key: "teams",
        header: "Týmy (aktivní)",
        render: (p) =>
          p.teams_count > 0 ? (
            <span>{p.teams_label}</span>
          ) : (
            <Text variant="muted">—</Text>
          ),
      },
      {
        key: "login_email",
        header: "Přihlašovací e-mail",
        // Persons hold no e-mail of their own — this is the linked User's
        // login address, empty for the many members who never log in.
        render: (p) =>
          p.login_email ? <span>{p.login_email}</span> : <Text variant="muted">—</Text>,
      },
      ...(canWrite
        ? [
            {
              key: "actions",
              header: "",
              render: (p: PersonListItem) => (
                <Table.Actions>
                  <Table.Action appearance="button" onClick={() => setEditing(p)}>Upravit</Table.Action>
                  <Table.Action appearance="button" onClick={() => setAddingToTeam(p)}>
                    Přidat do týmu
                  </Table.Action>
                </Table.Actions>
              ),
            },
          ]
        : []),
    ],
    [activeSeason, photoVersion, canWrite],
  );

  // Server mode: the list is offset-paginated, so the backend orders the
  // whole set (allow-list in internal/handlers/api/persons.go). The hook
  // holds only the URL state and hands the wire value to the query — it
  // never touches rows, and its tableProps carry no `rows` key.
  const tableSort = useTableSort<PersonListItem>(undefined, {
    columns,
    mode: "server",
    searchParams,
    setSearchParams,
    defaultSort: { key: "name", dir: "asc" },
  });

  // `error` and `refetch`, not just `isError` — see AuditLogPage.
  const {
    data,
    isLoading,
    error,
    refetch,
    pagination,
    setFilter,
  } = useOffsetPaginatedQuery(
    useGlobalPersons,
    {
      q: q || undefined,
      member: member || undefined,
      // The wire value the server allow-list resolved; absent when the
      // sort is the default, so the URL stays clean.
      ...tableSort.sortParams,
    },
    {
      searchParams,
      setSearchParams,
      defaultLimit: DEFAULT_LIMIT,
      limitOptions: LIMIT_OPTIONS,
    },
  );

  const {
    draft: qDraft,
    setDraft: setQDraft,
  } = useDebouncedSearchFilter({
    value: q,
    onCommit: (next) => setFilter("q", next || null),
  });

  const items = (data?.items ?? []) as PersonListItem[];
  const total = data?.total ?? 0;


  return (
    <>
      <AppTopbar crumbs={[{ label: "Členská základna" }]} />
      <Page>
        <Page.Header
          title="Členská základna"
          subtitle="globální evidence osob napříč týmy"
          actions={
            // The duplicates lists are read by Čtení všeho too; deciding a
            // pair stays with the writers, on that page.
            hasAnyGlobalRole(me, "admin", "members_admin", "global_viewer") && (
              <Button onClick={() => navigate("/clenska-zakladna/duplicity")}>Možné duplicity</Button>
            )
          }
        />
        <Page.Content>
          <FilterPanel>
            <FilterField wide>
              <Input
                label="Hledat"
                type="search"
                value={qDraft}
                placeholder="jméno, příjmení, přihlašovací e-mail nebo ČNSMS ID"
                onChange={(e) => setQDraft(e.target.value)}
              />
            </FilterField>
            <FilterField>
              <Select
                label="Členství"
                value={member}
                onChange={(e) => setFilter("member", e.target.value || null)}
              >
                <option value="">Všichni</option>
                <option value="1">{MEMBERSHIP_KIND_LABELS.registered}</option>
                <option value="0">{MEMBERSHIP_KIND_LABELS.guest}</option>
              </Select>
            </FilterField>
          </FilterPanel>

          <Page.SectionHeader
            title="Osoby"
            meta={total > 0 ? `${items.length} z ${total}` : undefined}
          />

          <Table
            framed
            {...tableSort.tableProps}
            columns={columns}
            rows={items}
            rowKey={(r) => r.id}
            isLoading={isLoading}
            error={error}
            onRetry={refetch}
            empty={
              <EmptyState
                title="Žádné osoby"
                description="Pro zadané filtry neexistují žádné osoby v evidenci."
              />
            }
          />

        </Page.Content>
        <Page.Footer>
          <Pagination {...pagination} />
        </Page.Footer>
      </Page>
      {editing && (
        <EditPersonModal
          person={editing}
          open
          onClose={() => {
            setPhotoVersion((v) => ({ ...v, [editing.id]: (v[editing.id] ?? 0) + 1 }));
            setEditing(null);
          }}
        />
      )}
      {addingToTeam && (
        <AddToTeamModal
          person={addingToTeam}
          open
          onClose={() => setAddingToTeam(null)}
        />
      )}
    </>
  );
}

